import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { autocompleteService } from '@/school-management/services/autocomplete';
import {
  TeacherAutocompleteItem,
  StudentAutocompleteItem,
  GroupAutocompleteItem,
  ClassCodeAutocompleteItem,
} from '@/school-management/types/response/autocomplete';
import type { CascadingFilterRequest } from '@/school-management/types/response/autocomplete';
import styles from './BimloaFilter.module.scss';

export type AutocompleteType = 'teachers' | 'students' | 'groups' | 'grades' | 'class_code';

export type AutocompleteItem =
  | TeacherAutocompleteItem
  | StudentAutocompleteItem
  | GroupAutocompleteItem
  | ClassCodeAutocompleteItem;

interface AutocompleteInputProps {
  value?: AutocompleteItem;
  onChange: (value: AutocompleteItem) => void;
  placeholder?: string;
  className?: string;
  organizationId: string;
  autocompleteType: AutocompleteType;
  cascadingFilters?: CascadingFilterRequest;
}

export const getDisplayText = (
  item: AutocompleteItem,
  autocompleteType: AutocompleteType
): string => {
  if (!item) {
    return '';
  }

  if (autocompleteType === 'teachers') {
    const teacher = item as TeacherAutocompleteItem;
    return `${teacher.firstName} ${teacher.lastName}`.trim();
  } else if (autocompleteType === 'students') {
    const student = item as StudentAutocompleteItem;
    return `${student.firstName} ${student.lastName}`.trim() || student.username;
  } else if (autocompleteType === 'groups') {
    const group = item as GroupAutocompleteItem;
    return group.groupName;
  } else if (autocompleteType === 'class_code') {
    const classCode = item as ClassCodeAutocompleteItem;
    return classCode;
  }

  return '';
};

const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  value,
  onChange,
  placeholder,
  className,
  organizationId,
  autocompleteType,
  cascadingFilters,
}) => {
  const { t } = useAdminTranslation();
  const [suggestions, setSuggestions] = useState<AutocompleteItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [displayValue, setDisplayValue] = useState<string>('');

  const valueText = useMemo(() => {
    if (!value) {
      return '';
    }

    return getDisplayText(value, autocompleteType);
  }, [value, autocompleteType]);

  useEffect(() => {
    setDisplayValue(valueText);
  }, [valueText]);

  const fetchSuggestions = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm.trim() || !organizationId) {
        setSuggestions([]);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        let response;
        switch (autocompleteType) {
          case 'teachers':
            response = await autocompleteService.getTeachers(
              organizationId,
              searchTerm,
              cascadingFilters
            );
            break;
          case 'students':
            response = await autocompleteService.getStudents(
              organizationId,
              searchTerm,
              cascadingFilters
            );
            break;
          case 'groups':
            response = await autocompleteService.getGroups(
              organizationId,
              searchTerm,
              cascadingFilters
            );
            break;
          case 'class_code':
            response = await autocompleteService.getClassCodes(
              organizationId,
              searchTerm,
              cascadingFilters
            );
            break;
          default:
            setSuggestions([]);
            setIsOpen(false);
            return;
        }

        if (response.status === 'success') {
          setSuggestions(response.data || []);
          setIsOpen(response.data && response.data.length > 0);
        } else {
          setError(response.message || t('bimloaFilter.failedToFetchSuggestions'));
          setSuggestions([]);
          setIsOpen(false);
        }
      } catch (err: any) {
        console.error('Autocomplete error:', err);
        setError(t('bimloaFilter.failedToLoadSuggestions'));
        setSuggestions([]);
        setIsOpen(false);
      } finally {
        setIsLoading(false);
      }
    },
    [organizationId, autocompleteType, cascadingFilters]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setDisplayValue(newValue);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (newValue.trim().length >= 2) {
      debounceTimerRef.current = setTimeout(() => {
        fetchSuggestions(newValue);
      }, 300);
    } else {
      setSuggestions([]);
      setIsOpen(false);
    }
  };

  const handleSelect = (item: AutocompleteItem) => {
    onChange(item);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : suggestions.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const handleFocus = () => {
    if (valueText.trim().length >= 2 && suggestions.length > 0) {
      setIsOpen(true);
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    }, 200);
  };

  const getSubtitle = (item: AutocompleteItem): string | null => {
    if (autocompleteType === 'teachers') {
      const teacher = item as TeacherAutocompleteItem;
      return teacher.email;
    } else if (autocompleteType === 'students') {
      const student = item as StudentAutocompleteItem;
      return student.email || student.username;
    } else if (autocompleteType === 'groups') {
      const group = item as GroupAutocompleteItem;
      return group.teachers && group.teachers.length > 0
        ? group.teachers.map(t => `${t.firstName} ${t.lastName}`).join(', ')
        : null;
    }
    return null;
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <div ref={containerRef} className={styles.autocompleteContainer}>
      <input
        ref={inputRef}
        type='text'
        className={className || styles.textInput}
        placeholder={placeholder}
        value={displayValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
      {isOpen && (
        <div className={styles.autocompleteDropdown}>
          {isLoading && <div className={styles.autocompleteLoading}>Loading...</div>}
          {error && <div className={styles.autocompleteError}>{error}</div>}
          {!isLoading && !error && suggestions.length === 0 && displayValue.trim().length >= 2 && (
            <div className={styles.autocompleteEmpty}>No results found</div>
          )}
          {!isLoading &&
            !error &&
            suggestions.map((item, index) => (
              <div
                key={
                  autocompleteType === 'teachers'
                    ? (item as TeacherAutocompleteItem).id
                    : autocompleteType === 'students'
                      ? (item as StudentAutocompleteItem).id
                      : autocompleteType === 'groups'
                        ? (item as GroupAutocompleteItem).groupId
                        : (item as ClassCodeAutocompleteItem)
                }
                className={`${styles.autocompleteItem} ${
                  index === selectedIndex ? styles.autocompleteItemSelected : ''
                }`}
                onClick={() => handleSelect(item)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className={styles.autocompleteItemText}>
                  {getDisplayText(item, autocompleteType)}
                </div>
                {getSubtitle(item) && (
                  <div className={styles.autocompleteItemSubtitle}>{getSubtitle(item)}</div>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default AutocompleteInput;
